import { environment } from "../config/environment";
import { getFromBackendAPI } from "./api.service";

/**
 * Read-side view of a task definition. The addin only uses these for the
 * "create agent" picker — creating/editing definitions lives on a separate
 * web app, so we don't surface mutation methods here.
 */
export interface TaskDefinitionPrerequisite {
  /** Short label used as the checkbox text and for dedupe across selected agents. */
  title: string;
  /** Longer helper text — usually what happens if the user runs without this in place. */
  description?: string;
}

export interface TaskDefinitionDto {
  id: string;
  name: string;
  description?: string;
  category?: string;
  systemPrompt: string;
  userPrompt: string;
  outputType: string;
  enabledTools: string[];
  /** "private" | "office" | "system". */
  visibility: TaskDefinitionVisibility;
  ownerEmail?: string;
  ownerOffice?: string;
  /**
   * User-facing checklist shown between "select agents" and "confirm" in the
   * create-agent dialog. Empty array when the definition has no prerequisites —
   * the addin auto-skips the prereqs step in that case.
   */
  prerequisites: TaskDefinitionPrerequisite[];
  createdAt: string;
  updatedAt: string;
  /** True when the calling user is the owner — irrelevant in the addin but the API returns it. */
  canEdit: boolean;
}

export type TaskDefinitionVisibility = "private" | "office" | "system";

/**
 * List every task definition visible to the caller: their own +
 * office-shared-to-them + system. Server-side search/category filtering is
 * supported via query params; the addin does most filtering locally.
 */
export async function getTaskDefinitions(params?: {
  search?: string;
  category?: string;
}): Promise<TaskDefinitionDto[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set("search", params.search);
  if (params?.category) query.set("category", params.category);
  const qs = query.toString();
  const endpoint = `${environment.api.baseUrl}/api/task-definitions${qs ? `?${qs}` : ""}`;
  return await getFromBackendAPI<TaskDefinitionDto[]>(endpoint);
}
